import React from 'react';

import { Stack, Typography } from '@mui/material';

import currencyFormatter from '~/utils/currencyFormatter';

export type DiscountBreakdownItem = {
  categoryBK?: string | null;
  subCategoryBK?: string | null;
  amount?: number | null;
  discountableTotal?: number | null;
};

type DiscountBreakdownColumn = 'amount' | 'discountableTotal' | 'percentage';

type DiscountBreakdownProps = {
  currencyCode?: string | null;
  items: Array<DiscountBreakdownItem>;
  column: DiscountBreakdownColumn;
  orderDiscountAmount?: number | null;
};

export function getDiscountPercentage(amount?: number | null, discountableTotal?: number | null): number | null {
  if (amount == null || discountableTotal == null || discountableTotal <= 0) {
    return null;
  }

  return (amount / discountableTotal) * 100;
}

function getItemLabel(item: DiscountBreakdownItem): string {
  if (!item.categoryBK) {
    return 'Item';
  }

  return item.subCategoryBK ? `${item.categoryBK}/${item.subCategoryBK}` : item.categoryBK;
}

export function PromoDiscountBreakdown({ currencyCode, items, column, orderDiscountAmount }: DiscountBreakdownProps) {
  if (column === 'amount' && orderDiscountAmount != null) {
    return (
      <Stack spacing={0.5} sx={{ py: 0.5 }}>
        <Typography variant="body2">Order: {currencyFormatter(currencyCode, orderDiscountAmount)}</Typography>
        {items.map((item, index) => (
          <Typography key={`${getItemLabel(item)}-${index}`} variant="body2">
            {getItemLabel(item)}{' '}
            <Typography component="span" color="success.main">
              {item.amount == null ? '—' : currencyFormatter(currencyCode, item.amount)}
            </Typography>
          </Typography>
        ))}
      </Stack>
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <Stack spacing={0.5} sx={{ py: 0.5 }}>
      {items.map((item, index) => {
        const percentage = getDiscountPercentage(item.amount, item.discountableTotal);

        if (column === 'discountableTotal') {
          return (
            <Typography key={`${getItemLabel(item)}-${index}`} variant="body2">
              {item.discountableTotal == null ? '—' : currencyFormatter(currencyCode, item.discountableTotal)}
            </Typography>
          );
        }

        if (column === 'percentage') {
          return (
            <Typography
              key={`${getItemLabel(item)}-${index}`}
              variant="body2"
              color={percentage != null && percentage > 20 ? 'error.main' : 'text.primary'}
            >
              {percentage == null ? '—' : `${percentage.toFixed(1)}%`}
            </Typography>
          );
        }

        return (
          <Typography key={`${getItemLabel(item)}-${index}`} variant="body2">
            {getItemLabel(item)}{' '}
            <Typography component="span" color="success.main">
              {item.amount == null ? '—' : currencyFormatter(currencyCode, item.amount)}
            </Typography>
          </Typography>
        );
      })}
    </Stack>
  );
}
